import React, { useState } from "react";
import { CheckCircle2, AlertCircle, Edit3, Rocket, Loader2, Eye, X, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STEPS = [
  { id: 1, name: "Property Profile", key: "propertyProfile" },
  { id: 2, name: "Building Access", key: "buildingAccess" },
  { id: 3, name: "Contact Details", key: "contactDetails" },
  { id: 4, name: "Check-in", key: "checkIn" },
  { id: 5, name: "Check-out", key: "checkOut" },
  { id: 6, name: "Luggage Storage", key: "luggageStorage" },
  { id: 7, name: "WiFi & Tech", key: "wifiTech" },
  { id: 8, name: "Heating & Utilities", key: "climateUtilities" },
  { id: 9, name: "Living Spaces", key: "livingSpaces" },
  { id: 10, name: "Kitchen & Dining", key: "kitchenDining" },
  { id: 11, name: "Bathroom & Laundry", key: "bathroomLaundry" },
  { id: 12, name: "Parking & Transport", key: "parkingTransport" },
  { id: 13, name: "House Rules", key: "houseRules" },
  { id: 14, name: "Local Guide", key: "localGuide" },
  { id: 15, name: "Safety & Emergency", key: "safetyMaintenance" },
  { id: 16, name: "Policies", key: "policies" },
  { id: 17, name: "Custom Knowledge", key: "customKnowledge" },
];

export const Step17 = ({ data, goToStep, isLight, api, isStepValid, isStepTouched, isSaving, handleSave, completedSteps }) => {
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewChunks, setPreviewChunks] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const isPublished = completedSteps.includes(18);

  const handlePreview = async () => {
    setIsPreviewing(true);
    setError(null);
    try {
      const res = await api.post('/preview-chunks', { knowledgeData: data });
      if (res.data?.ok) {
        setPreviewChunks(res.data.chunks);
        setShowPreviewModal(true);
      } else {
        throw new Error(res.data?.error || "Failed to generate preview");
      }
    } catch (err) {
      console.error("Preview error", err);
      setError(err.message || "Could not generate preview.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isPublished) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center py-20 text-center gap-6"
      >
        <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-xl ${isLight ? 'bg-green-100 text-green-600' : 'bg-green-500/20 text-green-400'}`}>
          <Rocket className="w-12 h-12" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className={`text-4xl font-bold ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>Knowledge Base Saved!</h2>
          <p className={`text-lg max-w-md ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>Your property's unique details have been processed and indexed for the AI assistant.</p>
        </div>
        
      </motion.div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {STEPS.map((step) => {
          const isValid = isStepValid(step.id);
          const isTouched = isStepTouched(step.id);

          return (
            <div key={step.id} className={`p-5 rounded-3xl flex items-center justify-between border transition-all ${
              isLight ? 'bg-white border-neutral-200 hover:border-blue-500/30 shadow-sm' : 'bg-white/5 border-white/5 hover:border-blue-400/30 shadow-lg'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center border transition-all ${isValid 
                  ? (isLight ? "bg-green-50 border-green-100 text-green-600" : "bg-green-500/10 border-green-500/20 text-green-400") 
                  : (isLight ? "bg-amber-50 border-amber-100 text-amber-600" : "bg-amber-500/10 border-amber-500/20 text-amber-400")}`}>
                  {isValid ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                </div>
                <div>
                  <h4 className={`text-sm font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>{step.name}</h4>
                  <span className={`text-[10px] font-black uppercase tracking-widest ${isValid ? "text-green-500/80" : "text-amber-500/80"}`}>
                    {isValid ? "Ready" : "Incomplete"}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => goToStep(step.id)}
                className={`p-2.5 rounded-xl transition-all ${isLight ? 'text-neutral-300 hover:bg-neutral-50 hover:text-blue-500' : 'text-neutral-600 hover:bg-white/5 hover:text-blue-400'}`}
              >
                <Edit3 size={18} />
              </button>
            </div>
          );
        })}
      </div>



      {/* Preview Modal */}
      <AnimatePresence>
        {showPreviewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPreviewModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-3xl shadow-2xl flex flex-col ${
                isLight ? 'bg-white' : 'bg-neutral-900 border border-white/10'
              }`}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-6 border-b ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
                <div>
                  <h3 className={`text-xl font-bold ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>AI Context Preview</h3>
                  <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    Total Chunks: {previewChunks.length} • These are the natural language templates generated for indexing.
                  </p>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className={`p-2 rounded-xl transition-all ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-white/5 text-neutral-500'}`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Body */}
              <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${isLight ? 'bg-neutral-50/50' : 'bg-black/20'}`}>
                {previewChunks.map((chunk, idx) => (
                  <div 
                    key={idx} 
                    className={`p-5 rounded-2xl border transition-all group relative ${
                      isLight 
                        ? 'bg-white border-neutral-200 hover:border-blue-200 shadow-sm' 
                        : 'bg-white/5 border-white/5 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-2.5 py-1 rounded-lg ${
                        isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-white/5 text-neutral-400'
                      }`}>
                        Chunk {idx + 1}
                      </span>
                      <button 
                        onClick={() => copyToClipboard(chunk)}
                        className={`opacity-0 group-hover:opacity-100 transition-all p-1.5 rounded-lg ${
                          isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-white/10 text-neutral-500'
                        }`}
                        title="Copy chunk text"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className={`text-sm leading-relaxed font-medium ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>
                      {typeof chunk === 'string' ? chunk : (chunk.content || "No content")}
                    </p>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className={`p-6 border-t flex justify-end ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className={`px-8 py-3 rounded-2xl font-bold text-sm transition-all ${
                    isLight 
                      ? 'bg-neutral-900 text-white hover:bg-neutral-800' 
                      : 'bg-white text-black hover:bg-neutral-100'
                  }`}
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
